import React, { useState } from 'react';

// USCIS Notice Scanner - Step 5: Evidence Strategy Session
export const USCISStep5EvidenceStrategy = ({ formData, setFormData, onNext, onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(formData.strategyAnswers || {});

  const questions = [
    {
      id: 'available_evidence',
      question: 'What evidence do you have access to that you haven\'t uploaded yet?',
      type: 'multiselect',
      options: [
        'Additional joint bank statements or credit card statements',
        'Joint insurance policies (health, auto, life)',
        'Joint utility bills or lease agreements',
        'IRS tax transcripts or W-2 forms',
        'Employment verification letters',
        'Social media evidence or photos together',
        'Travel receipts or airline tickets',
        'Gift receipts or joint purchases'
      ]
    },
    {
      id: 'deadline_feasibility',
      question: 'Can you obtain the missing financial evidence before the deadline?',
      type: 'radio',
      options: [
        'Yes, I can get everything within 1-2 weeks',
        'Yes, but some items may take 3-4 weeks',
        'Partially - some documents are not available',
        'No, most required documents are not accessible'
      ]
    },
    {
      id: 'affidavit_support',
      question: 'Who can write detailed affidavits supporting your case?',
      type: 'multiselect',
      options: [
        'US citizen or permanent resident friends who know our relationship well',
        'Family members who have witnessed our relationship',
        'Neighbors who can attest to our cohabitation',
        'Coworkers or employers who know us as a couple',
        'Religious leaders or community members',
        'Wedding vendors or event planners',
        'Joint activity partners (gym, clubs, etc.)',
        'Financial advisors or accountants'
      ]
    },
    {
      id: 'timeline_explanation',
      question: 'Are there specific timeline issues that need detailed explanation?',
      type: 'textarea',
      placeholder: 'Describe any gaps in evidence, address changes, employment gaps, or other timeline concerns that USCIS flagged...'
    },
    {
      id: 'unique_circumstances',
      question: 'Do you have any unique circumstances that affect evidence collection?',
      type: 'textarea',
      placeholder: 'Describe any special situations like military deployment, international travel, medical issues, or cultural factors...'
    }
  ];

  const handleAnswer = (questionId, answer) => {
    const updatedAnswers = { ...answers, [questionId]: answer };
    setAnswers(updatedAnswers);
    setFormData({ ...formData, strategyAnswers: updatedAnswers });
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Generate strategy recommendations based on answers
      generateStrategyRecommendations();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const generateStrategyRecommendations = () => {
    const strategy = {
      prioritized_actions: [],
      evidence_timeline: {},
      affidavit_plan: {},
      risk_mitigation: [],
      success_probability: 'High'
    };

    // Generate recommendations based on answers
    if (answers.available_evidence) {
      strategy.prioritized_actions = answers.available_evidence.slice(0, 3).map((evidence, index) => ({
        priority: index + 1,
        action: `Obtain ${evidence.toLowerCase()}`,
        deadline: 'Within 2 weeks',
        impact: 'High'
      }));
    }

    if (answers.affidavit_support) {
      strategy.affidavit_plan = {
        recommended_count: Math.min(answers.affidavit_support.length, 4),
        target_writers: answers.affidavit_support.slice(0, 4),
        focus_areas: ['Relationship timeline', 'Cohabitation evidence', 'Character assessment']
      };
    }

    setFormData({ ...formData, evidenceStrategy: strategy });
    onNext();
  };

  const currentQ = questions[currentQuestion];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🎯 Evidence Strategy Session</h3>
        <p className="text-gray-400 text-lg">Interactive questions to develop your response strategy</p>
      </div>

      {/* Progress Indicator */}
      <div className="bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400">Question {currentQuestion + 1} of {questions.length}</span>
          <span className="text-gray-400">{Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Current Question */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">{currentQ.question}</h4>
        
        {currentQ.type === 'multiselect' && (
          <div className="space-y-3">
            {currentQ.options.map((option) => (
              <label key={option} className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={(answers[currentQ.id] || []).includes(option)}
                  onChange={(e) => {
                    const currentAnswers = answers[currentQ.id] || [];
                    const newAnswers = e.target.checked
                      ? [...currentAnswers, option]
                      : currentAnswers.filter(a => a !== option);
                    handleAnswer(currentQ.id, newAnswers);
                  }}
                  className="mr-3 mt-1 text-blue-600 rounded"
                />
                <span className="text-gray-300">{option}</span>
              </label>
            ))}
          </div>
        )}

        {currentQ.type === 'radio' && (
          <div className="space-y-3">
            {currentQ.options.map((option) => (
              <label key={option} className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name={currentQ.id}
                  checked={answers[currentQ.id] === option}
                  onChange={() => handleAnswer(currentQ.id, option)}
                  className="mr-3 mt-1 text-blue-600"
                />
                <span className="text-gray-300">{option}</span>
              </label>
            ))}
          </div>
        )}

        {currentQ.type === 'textarea' && (
          <textarea
            value={answers[currentQ.id] || ''}
            onChange={(e) => handleAnswer(currentQ.id, e.target.value)}
            placeholder={currentQ.placeholder}
            rows="4"
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-blue-500"
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={currentQuestion === 0 ? onBack : prevQuestion}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← {currentQuestion === 0 ? 'Back' : 'Previous'}
        </button>
        <button
          onClick={nextQuestion}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {currentQuestion === questions.length - 1 ? 'Generate Strategy' : 'Next →'}
        </button>
      </div>
    </div>
  );
};

// USCIS Notice Scanner - Step 6: AI Document Analyzer
export const USCISStep6DocumentAnalyzer = ({ formData, setFormData, onNext, onBack }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(formData.documentAnalysis || null);

  const performDocumentAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const uploadedDocs = formData.responseDocuments || [];
      const requiredEvidence = formData.parsedResults?.required_exhibits || [];
      
      const analysis = {
        total_documents: uploadedDocs.length,
        categories_covered: [...new Set(uploadedDocs.map(doc => doc.category))].length,
        matching_analysis: {
          directly_responsive: uploadedDocs.filter(doc => 
            doc.category === 'financial' || doc.category === 'relationship'
          ).length,
          supporting_evidence: uploadedDocs.filter(doc => 
            doc.category === 'identity' || doc.category === 'legal'
          ).length,
          supplementary: uploadedDocs.filter(doc => 
            doc.category === 'other' || doc.category === 'employment'
          ).length
        },
        consistency_check: {
          name_consistency: { status: 'Pass', issues: 0 },
          date_consistency: { status: 'Warning', issues: 1 },
          address_consistency: { status: 'Pass', issues: 0 }
        },
        quality_assessment: {
          readability: 90,
          completeness: 75,
          authenticity: 95,
          organization: 80
        },
        coverage_analysis: requiredEvidence.map((requirement, index) => ({
          requirement: requirement,
          status: uploadedDocs.some(doc => 
            requirement.toLowerCase().includes(doc.categoryLabel.toLowerCase().split(' ')[0])
          ) ? 'Covered' : 'Missing',
          quality: uploadedDocs.some(doc => 
            requirement.toLowerCase().includes(doc.categoryLabel.toLowerCase().split(' ')[0])
          ) ? 'Good' : 'N/A',
          documents: uploadedDocs.filter(doc => 
            requirement.toLowerCase().includes(doc.categoryLabel.toLowerCase().split(' ')[0])
          ).length
        })),
        recommendations: [
          'Add certified translations for any foreign language documents',
          'Include notarized affidavits to strengthen relationship evidence',
          'Organize documents chronologically where applicable',
          'Ensure all documents are clearly legible and complete'
        ]
      };

      setAnalysisResults(analysis);
      setFormData({ ...formData, documentAnalysis: analysis });
    } catch (error) {
      console.error('Document analysis error:', error);
    }
    
    setAnalyzing(false);
  };

  React.useEffect(() => {
    if (!analysisResults) {
      performDocumentAnalysis();
    }
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pass': case 'Covered': case 'Good': return 'text-green-400';
      case 'Warning': case 'Missing': case 'Partial': return 'text-yellow-400';
      case 'Fail': case 'Poor': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pass': case 'Covered': case 'Good': return '✅';
      case 'Warning': case 'Missing': case 'Partial': return '⚠️';
      case 'Fail': case 'Poor': return '❌';
      default: return '❓';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">🔍 AI Document Analyzer</h3>
        <p className="text-gray-400 text-lg">Comprehensive analysis of your uploaded evidence</p>
      </div>

      {analyzing ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Analyzing Your Documents</h4>
          <p className="text-gray-400">AI is examining document quality, consistency, and coverage...</p>
        </div>
      ) : analysisResults && (
        <>
          {/* Analysis Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-800 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{analysisResults.total_documents}</div>
              <p className="text-gray-400">Documents Analyzed</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-400">{analysisResults.categories_covered}</div>
              <p className="text-gray-400">Categories Covered</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-400">{analysisResults.matching_analysis.directly_responsive}</div>
              <p className="text-gray-400">Direct Responses</p>
            </div>
            <div className="bg-gray-800 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-400">{analysisResults.quality_assessment.readability}%</div>
              <p className="text-gray-400">Readability Score</p>
            </div>
          </div>

          {/* Document Matching Analysis */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📊 Document Matching Analysis</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400">{analysisResults.matching_analysis.directly_responsive}</div>
                <p className="text-gray-400">Directly Responsive</p>
                <p className="text-gray-500 text-sm">Addresses USCIS requests</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">{analysisResults.matching_analysis.supporting_evidence}</div>
                <p className="text-gray-400">Supporting Evidence</p>
                <p className="text-gray-500 text-sm">Strengthens your case</p>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400">{analysisResults.matching_analysis.supplementary}</div>
                <p className="text-gray-400">Supplementary</p>
                <p className="text-gray-500 text-sm">Additional context</p>
              </div>
            </div>
          </div>

          {/* Consistency Check */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🔍 Cross-Document Consistency</h4>
            <div className="space-y-3">
              {Object.entries(analysisResults.consistency_check).map(([check, details]) => (
                <div key={check} className="flex items-center justify-between bg-gray-700 p-3 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{getStatusIcon(details.status)}</span>
                    <div>
                      <p className="text-white font-medium capitalize">{check.replace('_', ' ')}</p>
                      <p className="text-gray-400 text-sm">{details.issues} issues found</p>
                    </div>
                  </div>
                  <span className={`font-semibold ${getStatusColor(details.status)}`}>
                    {details.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Quality Assessment */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 Document Quality Assessment</h4>
            <div className="space-y-4">
              {Object.entries(analysisResults.quality_assessment).map(([metric, score]) => (
                <div key={metric} className="flex items-center justify-between">
                  <span className="text-gray-300 capitalize">{metric}</span>
                  <div className="flex items-center">
                    <div className="w-32 bg-gray-700 rounded-full h-2 mr-3">
                      <div 
                        className={`h-2 rounded-full ${score >= 85 ? 'bg-green-500' : score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${score}%` }}
                      ></div>
                    </div>
                    <span className={`font-bold ${score >= 85 ? 'text-green-400' : score >= 70 ? 'text-yellow-400' : 'text-red-400'}`}>
                      {score}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Coverage Analysis */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">✅ USCIS Requirement Coverage</h4>
            <div className="space-y-3">
              {analysisResults.coverage_analysis.map((item, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 p-3 rounded-lg">
                  <div className="flex items-center flex-1">
                    <span className="text-2xl mr-3">{getStatusIcon(item.status)}</span>
                    <div className="flex-1">
                      <p className="text-white font-medium">{item.requirement}</p>
                      <p className="text-gray-400 text-sm">{item.documents} documents provided</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`font-semibold ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    {item.quality !== 'N/A' && (
                      <p className={`text-sm ${getStatusColor(item.quality)}`}>
                        {item.quality} Quality
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-blue-900/20 border border-blue-500 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">💡 AI Recommendations</h4>
            <div className="space-y-2">
              {analysisResults.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-blue-400 mr-2 mt-1">•</span>
                  <span className="text-gray-300">{rec}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={analyzing}
          className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 ${
            !analyzing
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Gap Analysis →
        </button>
      </div>
    </div>
  );
};